// src/components/FeaturedGrid.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import useTranslationStatus from "../hooks/useTranslationStatus";
import { useTranslation } from "../i18n";

/* ═══════════════════════════════════════════
   CONSTANTS & HELPERS
═══════════════════════════════════════════ */
const API_BASE         = process.env.REACT_APP_API_BASE   || "http://localhost:8000/api";
const MEDIA_BASE       = process.env.REACT_APP_MEDIA_BASE || API_BASE.replace(/\/api\/?$/i, "");
const STORAGE_LANG_KEY = "site_lang";

const AUTO_REQUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const POLL_INTERVAL_MS         = 2500;
const POLL_MAX_ATTEMPTS        = 30;
/* module-scoped cooldown map */
const autoRequestedAt = new Map();

const normalizeLang = (l) => {
  try { return l ? String(l).split("-")[0].toLowerCase() : null; } catch { return l; }
};

const isRTLLang = (l) => ["ar","he","fa","ur"].includes(String(l||""));

const resolveImg = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${MEDIA_BASE}${url}`;
  return `${MEDIA_BASE}/${url}`;
};

const FALLBACK_SVG = (w=1600, h=900) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect width="100%" height="100%" fill="#1a1a18"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#333" font-size="48" font-family="sans-serif">24NEWS</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const getInitialLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_LANG_KEY);
    if (stored) return normalizeLang(stored);
    return normalizeLang(typeof navigator !== "undefined" ? navigator.language : "en") || "en";
  } catch { return "en"; }
};

const prefillTranslationFromItem = (item, lang) => {
  if (!item || !lang) return null;
  const meta = item.translations_meta || {};
  const m    = meta[lang];
  if (!m) return null;
  if (m.status === "done" && Array.isArray(item.translations)) {
    const full = item.translations.find((t) => String(t.target_lang).toLowerCase() === lang);
    if (full) return full;
  }
  return { status: m.status || "missing" };
};

const effectiveTranslation = (hookTr, prefill) => {
  if (!hookTr) return prefill || null;
  const ht = hookTr?.status ? hookTr : hookTr?.translation || hookTr;
  if (!ht) return prefill || null;
  if (ht.status === "missing" && prefill?.status && prefill.status !== "missing") return prefill;
  return ht;
};

const formatDate = (d, lang = "en") => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  try {
    return dt.toLocaleDateString(lang, { month: "short", day: "numeric", year: "numeric" });
  } catch { return dt.toLocaleDateString(); }
};

const timeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60)     return "just now";
  if (secs < 3600)   return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400)  return `${Math.floor(secs/3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs/86400)}d ago`;
  return "";
};

const estimateReadTime = (text="", wpm=200, t=null) => {
  const w = String(text||"").trim().split(/\s+/).filter(Boolean).length;
  if (!w) return null;
  const minutes = Math.max(1, Math.round(w/wpm));
  const label = t ? t("min") : "min";
  return `${minutes} ${label}`;
};

/* ═══════════════════════════════════════════
   TRANSLATION STATUS PILL
═══════════════════════════════════════════ */
const TrPill = ({ status, lang, loading }) => {
  
  if (loading) return (
    <span className="fg-tr-pill fg-tr-pill--loading">
      <span className="fg-tr-spin" />
    </span>
  );
  if (status === "done") return (
    <span className="fg-tr-pill fg-tr-pill--done">
      <i className="bi bi-translate" />{String(lang||"").toUpperCase()}
    </span>
  );
  if (status === "pending") return (
    <span className="fg-tr-pill fg-tr-pill--pending">
      <span className="fg-tr-spin" />Translating
    </span>
  );
  return null;
};

/* ═══════════════════════════════════════════
   TILE — single featured article card
═══════════════════════════════════════════ */
const Tile = ({
  item,
  size        = "secondary",   // "primary" | "secondary"
  isRTL       = false,
  lang        = "en",          // pass lang as prop
  translation,
  trLoading   = false,
  trRequesting,
  onRequestTranslation,
  priority    = false,         // eager-load image
}) => {
  // Hooks MUST be called at the top, before any conditional logic
  const { t } = useTranslation();
  const [imgErr,    setImgErr]    = useState(false);
  const [hovered,   setHovered]   = useState(false);
  const tileRef = useRef(null);

  if (!item) return null;

  const status         = translation?.status ? String(translation.status) : null;
  const displayedTitle = status==="done" ? (translation.title||item.title) : item.title;
  const displayedExcerpt = status==="done" ? (translation.excerpt||item.excerpt) : item.excerpt;
  const rawImg  = item.image || item.cover || item.thumbnail || null;
  const imgSrc  = imgErr ? FALLBACK_SVG() : (resolveImg(rawImg) || FALLBACK_SVG());
  const href    = `/${lang}/articles/${item.slug ?? item.id}`;
  const date    = item.published_at || item.created_at || null;
  const ago     = timeAgo(date);
  const readTime = item.reading_time || estimateReadTime(item.content||item.excerpt||"", 200, t);
  const cats    = Array.isArray(item.categories) ? item.categories.slice(0,2) : [];
  const views   = typeof item.views === "number" ? item.views : null;

  return (
    <Link
      ref={tileRef}
      to={href}
      className={[
        "fg-tile",
        `fg-tile--${size}`,
        isRTL ? "fg-rtl" : "",
      ].filter(Boolean).join(" ")}
      aria-label={displayedTitle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background image */}
      <div className="fg-tile-bg">
        <img
          src={imgSrc}
          alt=""
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          className="fg-tile-img"
          style={{ transform: hovered ? "scale(1.06)" : "scale(1)" }}
          onError={() => setImgErr(true)}
        />
      </div>

      {/* Gradient overlays — layered for depth */}
      <div className="fg-tile-grad fg-tile-grad--bottom" />
      <div className="fg-tile-grad fg-tile-grad--top" />

      {/* Top row: category + translation pill */}
      <div className="fg-tile-top">
        <div className="fg-tile-cats">
          {cats.map((c) => (
            <span
              key={c.id||c.slug}
              className="fg-cat-tag"
              onClick={(e) => e.preventDefault()}
            >
              {c.name}
            </span>
          ))}
        </div>
        <TrPill status={status} lang={lang} loading={trLoading} />
      </div>

      {/* Bottom content */}
      <div className="fg-tile-content">
        {/* Meta row */}
        <div className="fg-tile-meta">
          {ago && (
            <span className="fg-meta-item">
              <i className="bi bi-clock" aria-hidden />{ago}
            </span>
          )}
          {readTime && (
            <span className="fg-meta-item">
              <i className="bi bi-book" aria-hidden />{readTime} {t("read")}
            </span>
          )}
          {views !== null && (
            <span className="fg-meta-item">
              <i className="bi bi-eye" aria-hidden />
              {views >= 1000 ? `${(views/1000).toFixed(1)}k` : views}
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className={`fg-tile-title fg-tile-title--${size}`} dir={isRTL?"rtl":"ltr"}>
          {displayedTitle || "Untitled"}
        </h2>

        {/* Excerpt — only on primary tile */}
        {size === "primary" && displayedExcerpt && (
          <p className="fg-tile-excerpt" dir={isRTL?"rtl":"ltr"}>
            {displayedExcerpt}
          </p>
        )}

        {/* Read more caret */}
        <div className="fg-tile-cta">
          <span className="fg-cta-label">
            {lang==="ar" ? "اقرأ المزيد" : lang==="fr" ? "Lire" : lang==="de" ? "Lesen" : "Read more"}
          </span>
          <span className={`fg-cta-arrow${isRTL?" fg-cta-arrow--rtl":""}`} aria-hidden>
            <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"}`} />
          </span>
        </div>
      </div>
    </Link>
  );
};

/* ═══════════════════════════════════════════
   SKELETON TILE
═══════════════════════════════════════════ */
const TileSkeleton = ({ size="secondary" }) => (
  <div className={`fg-tile-skel fg-tile-skel--${size}`}>
    <div className="fg-skel-img fg-shimmer" />
    <div className="fg-skel-body">
      <div className="fg-shimmer" style={{width:"40%",height:10,borderRadius:4}} />
      <div className="fg-shimmer" style={{width:"88%",height:size==="primary"?22:18,borderRadius:5,marginTop:10}} />
      {size==="primary" && <>
        <div className="fg-shimmer" style={{width:"70%",height:18,borderRadius:5,marginTop:5}} />
        <div className="fg-shimmer" style={{width:"100%",height:13,borderRadius:4,marginTop:12}} />
        <div className="fg-shimmer" style={{width:"80%",height:13,borderRadius:4,marginTop:5}} />
      </>}
      <div className="fg-shimmer" style={{width:"28%",height:11,borderRadius:4,marginTop:16}} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   FEATURED GRID (MAIN)
═══════════════════════════════════════════ */
/**
 * Props
 * ─────────────────────────────────────
 * articles   — array of article objects (up to 3 shown)
 * fullBleed  — bool  extend to viewport edges  (default true)
 * loading    — bool  show skeletons
 * maxItems   — number  how many articles to feature  (default 3, max 3)
 */
const FeaturedGrid = ({ articles = [], fullBleed = true, loading = false, maxItems = 3 }) => {
  const items = useMemo(() =>
    Array.isArray(articles) ? articles.filter(Boolean).slice(0, Math.min(maxItems, 3)) : [],
  [articles, maxItems]);

  const n = items.length;

  const [lang,    setLang]    = useState(getInitialLang);
  const nLang = useMemo(() => normalizeLang(lang), [lang]);
  const isRTL = isRTLLang(nLang);

  /* token */
  const token = useMemo(() => {
    try {
      return localStorage.getItem("accessToken") ||
             sessionStorage.getItem("accessToken") ||
             localStorage.getItem("access_token")  ||
             sessionStorage.getItem("access_token") || null;
    } catch { return null; }
  }, []);

  /* stable item refs */
  const item0 = items[0] ?? null;
  const item1 = items[1] ?? null;
  const item2 = items[2] ?? null;

  /* prefill translations from payload */
  const pre0 = useMemo(() => prefillTranslationFromItem(item0, nLang), [item0, nLang]);
  const pre1 = useMemo(() => prefillTranslationFromItem(item1, nLang), [item1, nLang]);
  const pre2 = useMemo(() => prefillTranslationFromItem(item2, nLang), [item2, nLang]);

  /* translation hooks — always called in stable order */
  const hookOpts = useMemo(() => ({
    pollOnPending: true, pollInterval: POLL_INTERVAL_MS,
    authToken: token, withCredentials: false,
  }), [token]);

  const h0 = useTranslationStatus(item0 ? (item0.slug ?? item0.id) : null, nLang, hookOpts);
  const h1 = useTranslationStatus(item1 ? (item1.slug ?? item1.id) : null, nLang, hookOpts);
  const h2 = useTranslationStatus(item2 ? (item2.slug ?? item2.id) : null, nLang, hookOpts);

  /* effective translations */
  const eff0 = useMemo(() => effectiveTranslation(h0.translation, pre0), [h0.translation, pre0]);
  const eff1 = useMemo(() => effectiveTranslation(h1.translation, pre1), [h1.translation, pre1]);
  const eff2 = useMemo(() => effectiveTranslation(h2.translation, pre2), [h2.translation, pre2]);

  const loading0 = Boolean(h0.loading && (!pre0 || pre0.status === "missing"));
  const loading1 = Boolean(h1.loading && (!pre1 || pre1.status === "missing"));
  const loading2 = Boolean(h2.loading && (!pre2 || pre2.status === "missing"));

  /* poll timers */
  const pollTimersRef = useRef(new Map());

  const fetchTranslationStatus = useCallback(async (slug, l) => {
    if (!slug || !l) return null;
    const url = `${API_BASE}/articles/${encodeURIComponent(slug)}/translation_status/?lang=${encodeURIComponent(l)}`;
    const headers = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(url, { method:"GET", headers, credentials:"same-origin" });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  }, [token]);

  const startStatusPolling = useCallback((key, slug, l, hookRef) => {
    if (!key || !slug || !l || pollTimersRef.current.has(key)) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts++;
      try {
        const data = await fetchTranslationStatus(slug, l);
        const st   = data?.status || (Array.isArray(data) ? data[0]?.status : null);
        if (st === "done") {
          try { await hookRef.refetch(); } catch {}
          clearInterval(id); pollTimersRef.current.delete(key);
        } else if (attempts >= POLL_MAX_ATTEMPTS) {
          clearInterval(id); pollTimersRef.current.delete(key);
        }
      } catch {
        if (attempts >= POLL_MAX_ATTEMPTS) { clearInterval(id); pollTimersRef.current.delete(key); }
      }
    }, POLL_INTERVAL_MS);
    pollTimersRef.current.set(key, id);
  }, [fetchTranslationStatus]);

  useEffect(() => () => {
    for (const id of pollTimersRef.current.values()) { try { clearInterval(id); } catch {} }
    pollTimersRef.current.clear();
  }, []);

  /* auto-request helper */
  const shouldAutoRequest = (key) => {
    if (!key) return false;
    const ts = autoRequestedAt.get(key) || 0;
    return Date.now() - ts > AUTO_REQUEST_COOLDOWN_MS;
  };

  const autoRequest = useCallback((item, eff, hook, l) => {
    const slug = item ? (item.slug ?? item.id) : null;
    if (!slug) return;
    const key    = `${slug}::${l}`;
    const status = eff?.status;
    if (status === "pending") { startStatusPolling(key, slug, l, hook); return; }
    if (status === "missing" && !hook.requesting && shouldAutoRequest(key)) {
      autoRequestedAt.set(key, Date.now());
      hook.requestTranslation()
        .then(() => startStatusPolling(key, slug, l, hook))
        .catch(() => {});
    }
  }, [startStatusPolling]);

  useEffect(() => { autoRequest(item0, eff0, h0, nLang); }, [item0?.slug, item0?.id, nLang, eff0?.status, h0.requesting]);
  useEffect(() => { autoRequest(item1, eff1, h1, nLang); }, [item1?.slug, item1?.id, nLang, eff1?.status, h1.requesting]);
  useEffect(() => { autoRequest(item2, eff2, h2, nLang); }, [item2?.slug, item2?.id, nLang, eff2?.status, h2.requesting]);

  /* language sync */
  useEffect(() => {
    const handle = (e) => {
      try {
        const nl = e?.detail?.lang ?? e?.newValue ?? null;
        if (nl) setLang(String(nl));
        else { try { const v=localStorage.getItem(STORAGE_LANG_KEY); if(v) setLang(v); } catch {} }
      } catch {}
    };
    window.addEventListener("site:language_changed", handle);
    window.addEventListener("storage",               handle);
    return () => {
      window.removeEventListener("site:language_changed", handle);
      window.removeEventListener("storage",               handle);
    };
  }, []);

  /* ── Loading ── */
  if (loading) {
    return (
      <>
        <FeaturedGridStyles />
        <section className={`fg-section${fullBleed?" fg-section--bleed":""}`} aria-label="Featured articles">
          <div className="fg-loading-grid fg-loading-grid--3">
            <TileSkeleton size="primary" />
            <div className="fg-loading-stack">
              <TileSkeleton size="secondary" />
              <TileSkeleton size="secondary" />
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!n) return <FeaturedGridStyles />;

  /* ── Build tile data ── */
  const tileData = [
    { item: item0, eff: eff0, loading: loading0, hook: h0 },
    { item: item1, eff: eff1, loading: loading1, hook: h1 },
    { item: item2, eff: eff2, loading: loading2, hook: h2 },
  ].slice(0, n);

  const commonProps = (idx) => ({
    lang:        nLang,
    isRTL,
    translation: tileData[idx]?.eff,
    trLoading:   tileData[idx]?.loading,
    trRequesting: tileData[idx]?.hook.requesting,
    onRequestTranslation: () => tileData[idx]?.hook.requestTranslation(),
  });

  return (
    <>
      <FeaturedGridStyles />
      <section
        className={[
          "fg-section",
          fullBleed ? "fg-section--bleed" : "",
          isRTL     ? "fg-rtl"            : "",
        ].filter(Boolean).join(" ")}
        dir={isRTL ? "rtl" : "ltr"}
        aria-label="Featured articles"
      >
        {/* ══ 1 article: full-width cinematic ══ */}
        {n === 1 && (
          <div className="fg-layout fg-layout--solo">
            <Tile item={item0} size="primary" priority {...commonProps(0)} />
          </div>
        )}

        {/* ══ 2 articles: 7/5 split ══ */}
        {n === 2 && (
          <div className="fg-layout fg-layout--duo">
            <div className="fg-primary-col">
              <Tile item={item0} size="primary" priority {...commonProps(0)} />
            </div>
            <div className="fg-secondary-col">
              <Tile item={item1} size="secondary" {...commonProps(1)} />
            </div>
          </div>
        )}

        {/* ══ 3 articles: 7/5 with stacked secondary ══ */}
        {n === 3 && (
          <div className="fg-layout fg-layout--trio">
            <div className="fg-primary-col">
              <Tile item={item0} size="primary" priority {...commonProps(0)} />
            </div>
            <div className="fg-secondary-col">
              <Tile item={item1} size="secondary" {...commonProps(1)} />
              <Tile item={item2} size="secondary" {...commonProps(2)} />
            </div>
          </div>
        )}

        {/* Live region for translation changes */}
        <div aria-live="polite" aria-atomic="true" className="fg-sr-only">
          {[eff0, eff1, eff2].filter(Boolean).map((e, i) =>
            e?.status === "done" ? `Article ${i+1} translated` : null
          ).filter(Boolean).join(". ")}
        </div>
      </section>
    </>
  );
};

/* ═══════════════════════════════════════════
   STYLES — injected once
═══════════════════════════════════════════ */
let _fgInjected = false;
const FeaturedGridStyles = () => {
  useEffect(() => {
    if (_fgInjected || document.getElementById("fg-css")) return;
    _fgInjected = true;
    const el = document.createElement("style");
    el.id    = "fg-css";
    el.textContent = FG_CSS;
    document.head.appendChild(el);
  }, []);
  return null;
};

const FG_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

/* ── tokens ── */
:root {
  --fg-serif:   'Playfair Display', Georgia, 'Times New Roman', serif;
  --fg-sans:    'DM Sans', 'Helvetica Neue', sans-serif;
  --fg-red:     #dc2f14;
  --fg-gold:    #f59e0b;
  --fg-white:   #ffffff;
  --fg-black:   #0a0a09;
  --fg-ink3:    rgba(255,255,255,.55);
  --fg-gap:     3px;
  --fg-radius:  0px;              /* editorial / newspaper: no rounding */
  --fg-t-img:   transform 0.65s cubic-bezier(.4,0,.2,1);
  --fg-t-fast:  0.18s cubic-bezier(.4,0,.2,1);
  --fg-h-primary:   clamp(380px, 52vw, 580px);
  --fg-h-secondary: clamp(180px, 26vw, 290px);
}

/* ── sr-only ── */
.fg-sr-only {
  position:absolute; width:1px; height:1px;
  padding:0; margin:-1px; overflow:hidden;
  clip:rect(0,0,0,0); white-space:nowrap; border:0;
}

/* ── section ── */
.fg-section {
  position: relative;
  font-family: var(--fg-sans);
  overflow: hidden;
  /* add a thin red accent line at the top */
  border-top: 3px solid var(--fg-red);
}
.fg-section--bleed {
  margin-left:  calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  width: 100vw;
}

/* ════════════════════════════════════════
   LAYOUTS
════════════════════════════════════════ */
.fg-layout {
  display: flex;
  gap: var(--fg-gap);
  background: var(--fg-black);
}

/* Solo */
.fg-layout--solo { flex-direction: column; }
.fg-layout--solo .fg-tile--primary { height: var(--fg-h-primary); }

/* Duo */
.fg-layout--duo { flex-direction: row; }
.fg-layout--duo .fg-primary-col   { flex: 7; min-width: 0; }
.fg-layout--duo .fg-secondary-col { flex: 5; min-width: 0; }
.fg-layout--duo .fg-tile--primary   { height: var(--fg-h-primary); }
.fg-layout--duo .fg-tile--secondary { height: var(--fg-h-primary); }

/* Trio */
.fg-layout--trio { flex-direction: row; }
.fg-layout--trio .fg-primary-col   { flex: 7; min-width: 0; }
.fg-layout--trio .fg-secondary-col {
  flex: 5; min-width: 0;
  display: flex; flex-direction: column; gap: var(--fg-gap);
}
.fg-layout--trio .fg-tile--primary   { height: var(--fg-h-primary); }
.fg-layout--trio .fg-tile--secondary { flex: 1; min-height: var(--fg-h-secondary); }

/* ─── TABLET: stacks at ≤ 767px ─── */
@media (max-width: 767px) {
  :root {
    --fg-h-primary:   clamp(260px, 55vw, 380px);
    --fg-h-secondary: clamp(160px, 36vw, 220px);
  }

  .fg-layout--duo,
  .fg-layout--trio { flex-direction: column; }

  .fg-layout--duo .fg-secondary-col,
  .fg-layout--trio .fg-secondary-col {
    flex-direction: row;
    gap: var(--fg-gap);
  }

  /* Each secondary tile gets half width when side-by-side */
  .fg-layout--duo .fg-tile--secondary,
  .fg-layout--trio .fg-tile--secondary {
    flex: 1;
    height: var(--fg-h-secondary);
  }

  /* When only one secondary: full width */
  .fg-layout--duo .fg-secondary-col .fg-tile--secondary:only-child {
    flex: none;
    width: 100%;
  }
}

/* ─── PHONE: everything stacks ─── */
@media (max-width: 479px) {
  :root {
    --fg-h-primary:   clamp(220px, 62vw, 300px);
    --fg-h-secondary: clamp(150px, 44vw, 200px);
  }

  .fg-layout--duo .fg-secondary-col,
  .fg-layout--trio .fg-secondary-col { flex-direction: column; }

  .fg-layout--duo .fg-tile--secondary,
  .fg-layout--trio .fg-tile--secondary {
    flex: none;
    height: var(--fg-h-secondary);
    width: 100%;
  }
}

/* ════════════════════════════════════════
   TILE BASE
════════════════════════════════════════ */
.fg-tile {
  position: relative;
  display: block;
  overflow: hidden;
  text-decoration: none;
  color: var(--fg-white);
  outline: none;
  cursor: pointer;
  background: #111;
}
.fg-tile:focus-visible { outline: 2px solid var(--fg-red); outline-offset: -2px; }

/* ── Image ── */
.fg-tile-bg {
  position: absolute; inset: 0; overflow: hidden;
}
.fg-tile-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: var(--fg-t-img);
  will-change: transform;
  transform-origin: center;
}

/* ── Gradient overlays ── */
.fg-tile-grad {
  position: absolute; left: 0; right: 0;
  pointer-events: none;
}
.fg-tile-grad--bottom {
  bottom: 0; height: 70%;
  background: linear-gradient(
    to top,
    rgba(0,0,0,.88) 0%,
    rgba(0,0,0,.55) 45%,
    rgba(0,0,0,.0) 100%
  );
}
.fg-tile-grad--top {
  top: 0; height: 50%;
  background: linear-gradient(
    to bottom,
    rgba(0,0,0,.42) 0%,
    rgba(0,0,0,.0) 100%
  );
}
/* primary tile gets richer overlay */
.fg-tile--primary .fg-tile-grad--bottom {
  height: 75%;
  background: linear-gradient(
    to top,
    rgba(0,0,0,.92) 0%,
    rgba(0,0,0,.6) 40%,
    rgba(0,0,0,.0) 100%
  );
}

/* ── Top row ── */
.fg-tile-top {
  position: absolute; top: 0; left: 0; right: 0;
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: clamp(10px, 2.5vw, 18px);
  gap: 8px;
  z-index: 2;
}
.fg-rtl .fg-tile-top { flex-direction: row-reverse; }

.fg-tile-cats { display: flex; flex-wrap: wrap; gap: 5px; flex: 1; min-width: 0; }
.fg-cat-tag {
  display: inline-block;
  font-size: 0.6rem; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase;
  background: var(--fg-red); color: #fff;
  padding: 4px 8px; border-radius: 2px;
  white-space: nowrap;
  transition: background var(--fg-t-fast);
}
.fg-tile:hover .fg-cat-tag { background: #b52510; }

/* ── Translation pill ── */
.fg-tr-pill {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.62rem; font-weight: 700; letter-spacing: .04em;
  padding: 4px 9px; border-radius: 3px;
  backdrop-filter: blur(6px); flex-shrink: 0;
}
.fg-tr-pill--done    { background: rgba(22,163,74,.85); color: #fff; }
.fg-tr-pill--pending { background: rgba(245,158,11,.85); color: #000; }
.fg-tr-pill--loading { background: rgba(0,0,0,.5); }
.fg-tr-spin {
  width: 10px; height: 10px; border-radius: 50%;
  border: 1.5px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  animation: fg-spin .7s linear infinite;
  display: inline-block; flex-shrink: 0;
}
@keyframes fg-spin { to { transform: rotate(360deg); } }

/* ── Content ── */
.fg-tile-content {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px) clamp(16px, 3.5vw, 28px);
  z-index: 2;
  display: flex; flex-direction: column; gap: 6px;
}

/* Meta row */
.fg-tile-meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
}
.fg-meta-item {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.68rem; font-weight: 500;
  color: rgba(255,255,255,.6);
  letter-spacing: .02em;
}
.fg-meta-item i { font-size: 0.65rem; }

/* Title */
.fg-tile-title {
  font-family: var(--fg-serif);
  font-weight: 800;
  color: #fff;
  margin: 0;
  line-height: 1.22;
  letter-spacing: -.02em;
  text-shadow: 0 1px 4px rgba(0,0,0,.3);
}
.fg-tile-title--primary {
  font-size: clamp(1.125rem, 3.2vw, 1.875rem);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.fg-tile-title--secondary {
  font-size: clamp(0.9375rem, 2vw, 1.125rem);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}

/* Excerpt */
.fg-tile-excerpt {
  font-size: clamp(0.75rem, 1.4vw, 0.875rem);
  line-height: 1.6;
  color: rgba(255,255,255,.62);
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* CTA */
.fg-tile-cta {
  display: inline-flex; align-items: center; gap: 7px;
  margin-top: 4px;
}
.fg-cta-label {
  font-size: 0.72rem; font-weight: 700;
  color: var(--fg-red); letter-spacing: .05em; text-transform: uppercase;
  transition: color var(--fg-t-fast);
}
.fg-tile:hover .fg-cta-label { color: #ff6b35; }
.fg-cta-arrow {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  border: 1.5px solid var(--fg-red);
  color: var(--fg-red); font-size: 0.6rem;
  transition: transform var(--fg-t-fast), background var(--fg-t-fast), border-color var(--fg-t-fast);
}
.fg-tile:hover .fg-cta-arrow {
  background: var(--fg-red); color: #fff; border-color: var(--fg-red);
  transform: translateX(3px);
}
.fg-cta-arrow--rtl { transform: scaleX(-1); }
.fg-tile:hover .fg-cta-arrow--rtl { transform: scaleX(-1) translateX(-3px); }

/* ─── Phone: simplified content ─── */
@media (max-width: 479px) {
  .fg-tile-meta    { display: none; }
  .fg-tile-excerpt { display: none; }
  .fg-tile-cta     { display: none; }
  .fg-tile-title--secondary { -webkit-line-clamp: 2; }
  .fg-tile-content { gap: 4px; }
}

/* ─── Between 480–767: show meta but hide excerpt on secondary ─── */
@media (min-width: 480px) and (max-width: 767px) {
  .fg-tile--secondary .fg-tile-excerpt { display: none; }
  .fg-tile--secondary .fg-tile-cta     { display: none; }
  .fg-tile-meta { gap: 7px; }
}

/* ════════════════════════════════════════
   SKELETON
════════════════════════════════════════ */
@keyframes fg-shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}
.fg-shimmer {
  background: linear-gradient(90deg, #1e1e1c 25%, #2a2a28 50%, #1e1e1c 75%);
  background-size: 1200px 100%;
  animation: fg-shimmer 1.8s ease-in-out infinite;
  display: block;
}

.fg-loading-grid {
  display: flex; gap: var(--fg-gap);
  background: var(--fg-black);
}
.fg-loading-grid--3 { min-height: var(--fg-h-primary); }

.fg-loading-stack {
  flex: 5; min-width: 0;
  display: flex; flex-direction: column; gap: var(--fg-gap);
}

.fg-tile-skel { position: relative; overflow: hidden; }
.fg-tile-skel--primary  { flex: 7; min-width: 0; height: var(--fg-h-primary); }
.fg-tile-skel--secondary { flex: 1; min-height: var(--fg-h-secondary); }

.fg-skel-img { position: absolute; inset: 0; border-radius: 0; }

.fg-skel-body {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: clamp(14px,3vw,24px) clamp(14px,3vw,28px);
  display: flex; flex-direction: column; gap: 0;
}

@media (max-width: 767px) {
  .fg-loading-grid { flex-direction: column; }
  .fg-loading-stack { flex-direction: row; }
  .fg-tile-skel--primary  { height: var(--fg-h-primary); }
  .fg-tile-skel--secondary { height: var(--fg-h-secondary); }
}

@media (max-width: 479px) {
  .fg-loading-stack { flex-direction: column; }
  .fg-tile-skel--secondary { flex: none; width: 100%; }
}

/* ════════════════════════════════════════
   RTL
════════════════════════════════════════ */
.fg-rtl .fg-tile-content { text-align: right; }
.fg-rtl .fg-tile-meta    { flex-direction: row-reverse; }
.fg-rtl .fg-tile-cats    { flex-direction: row-reverse; justify-content: flex-end; }
.fg-rtl .fg-tile-cta     { flex-direction: row-reverse; }
.fg-rtl .fg-tile-title   { font-family: 'Noto Serif Arabic', 'Noto Sans Arabic', Georgia, serif; }

/* ════════════════════════════════════════
   PRINT
════════════════════════════════════════ */
@media print {
  .fg-section--bleed { margin-left: 0; margin-right: 0; width: auto; }
  .fg-tile-img       { filter: grayscale(100%); }
  .fg-tr-pill        { display: none; }
}
`;

export default FeaturedGrid;